import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { HiOutlineArrowCircleLeft } from "react-icons/hi";

import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from 'primereact/inputtextarea';
import { toast } from "react-toastify";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import Error from "../../../../../../components/Error";

import { newPlan, updatePlan } from "../../../../../../services/manager/signature.service";
import { Dropdown } from "primereact/dropdown";
import { ICommonTestComponent } from "../../../../../../models/ICommonTestComponent";

interface INewEditPlanComponent extends ICommonTestComponent { 
  back?: any,
  plan?: any,
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Este campo é obrigatório"),
  description: Yup.string().trim().required("Este campo é obrigatório"),
  total_price: Yup.number().required("Este campo é obrigatório"),
  parcel_max: Yup.number().required("Este campo é obrigatório"),
  materials_max: Yup.number().min(1, "É necessário colocar ao menos 1 disicplina").required("Este campo é obrigatório"),
  minimum_contract: Yup.number().required("Este campo é obrigatório"),
  url_checkout: Yup.string(),
}).required();

export const NewEditPlan: React.FC<INewEditPlanComponent> = ({back, plan}) => {
  const { t: translation } = useTranslation();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm({ resolver: yupResolver(validationSchema) });

  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [totalPriceValue, setTotalPriceValue] = useState<any>(null);
  const [materialsMaxValue, setMaterialsMaxValue] = useState<any>(null);
  const [parcelMaxValue, setParcelMaxValue] = useState<any>(null);
  const [contractValue, setContractValue] = useState<any>(null);

  const maxParcelOptions = [
    {label: "1x", value: 1},
    {label: "2x", value: 2},
    {label: "3x", value: 3},
    {label: "4x", value: 4},
    {label: "5x", value: 5},
    {label: "6x", value: 6},
    {label: "7x", value: 7},
    {label: "8x", value: 8},
    {label: "9x", value: 9},
    {label: "10x", value: 10},
    {label: "11x", value: 11},
    {label: "12x", value: 12},
    {label: "24x", value: 24},
    {label: "36x", value: 36},
  ]

  const minimumContractOptions = [
    {label: "1 mês", value: 1},
    {label: "3 meses", value: 3},
    {label: "6 meses", value: 6},
    {label: "12 meses", value: 12},
    {label: "24 meses", value: 24},
    {label: "36 meses", value: 36},
  ]

  const onSubmit = (formData: any) => {
    setLoadingButton(true)

    if (plan && plan.id) {
      updatePlan(plan.id, formData).then(() => {
        toast.success(`Plano alterado com sucesso!`)
        setLoadingButton(false)
        back(false);
      }).catch(() => {
        toast.error("Ocorreu um erro ao editar o plano. Tente novamente.")
        setLoadingButton(false)
      })
    } else {
      newPlan(formData).then(() => {
        toast.success("Plano adicionado com sucesso!")
        setLoadingButton(false)
        back(false);
      }).catch(() => {
        toast.error("Ocorreu um erro ao adicionar o plano. Tente novamente.")
        setLoadingButton(false)
      })
    }

  }

  useEffect(() => {
    if (plan) {
      setTotalPriceValue(plan.total_price)
      setMaterialsMaxValue(plan.materials_max)
      setParcelMaxValue(plan.parcel_max)
      setContractValue(plan.minimum_contract)

      setValue('name', plan.name)
      setValue('description', plan.description)
      setValue('total_price', plan.total_price)
      setValue('parcel_max', plan.parcel_max)
      setValue('materials_max', plan.materials_max)
      setValue('minimum_contract', plan.minimum_contract)
      setValue('url_checkout', plan.url_checkout)
    }
  }, [])

  return (
    <Row className="ml-2 mt-3 mr-4">
      <Col md={12} className="text-black mb-4">
        <HiOutlineArrowCircleLeft className="cursor-pointer mr-3 mb-1" size="25" 
          onClick={() => {
            back(false);
          }} 
        /> 
        <span style={{ fontSize: '20px' }}><b>{plan ? translation('manager.settings.editPlan') : translation('manager.settings.newPlan')}</b></span>
      </Col>

      <Col xs={12} md={12} lg={8} xl={7} xxl={5} className="text-black mb-4">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label htmlFor="name" className="block">Título</label>
            <InputText id="name" {...register("name", { required: true })} type="text" className="w-100" style={{ height: '40px'}} />
            {errors.name ? (<div className="w-100"> <Error>{errors.name?.message}</Error> </div>) : ""}
          </div>

          <div className="mb-3">
            <label htmlFor="description" className="block">Descrição</label>
            <InputTextarea className="w-100" rows={5} cols={30} {...register("description", { required: true })}  autoResize />
            {errors.description ? (<div className="w-100"> <Error>{errors.description?.message}</Error> </div>) : ""}
          </div>

          <Row>
            <Col md={6} className="mb-3">
              <label htmlFor="name" className="block">Valor Total</label>
              <InputNumber
                value={totalPriceValue}
                onValueChange={(e: any) => {
                  setTotalPriceValue(e.value); 
                  setValue('total_price', e.value)
                }}
                mode="currency" 
                currency="BRL" 
                locale="pt-BR"
                placeholder="R$ 10.000,00"
                style={{ width: '100%', height: '40px' }}
              />
              {errors.total_price ? (<div className="w-100"> <Error>{errors.total_price?.message}</Error> </div>) : ""}
            </Col>

            <Col md={6} className="mb-3">
              <label htmlFor="name" className="block">Número máximo de parcelas</label>
              <Dropdown
                className="w-100 select-form-default"
                options={maxParcelOptions}
                value={parcelMaxValue}
                onChange={(e) => {
                  setParcelMaxValue(e.value)
                  setValue('parcel_max', e.value)
                }} 
                placeholder="Máximo de parcelas"
              />
              {errors.parcel_max ? (<div className="w-100"> <Error>{errors.parcel_max?.message}</Error> </div>) : ""}
            </Col>

            <Col md={6} className="mb-3">
              <label htmlFor="materialMax">Limite de disciplinas</label>
              <InputNumber
                inputId="materialMax" 
                value={materialsMaxValue}
                onValueChange={(e) => {
                  if (e.value !== null) {
                    setMaterialsMaxValue(e.value)
                    setValue('materials_max', e.value)
                  }
                }} 
                className="w-100" 
                style={{ height: '40px'}} 
              />
              {errors.materials_max ? (<div className="w-100"> <Error>{errors.materials_max?.message}</Error> </div>) : ""}
            </Col>

            <Col md={6} className="mb-3">
              <label htmlFor="name" className="block">Contrato mínimo</label>
              <Dropdown
                className="w-100 select-form-default"
                options={minimumContractOptions}
                value={contractValue}
                onChange={(e) => {
                  setContractValue(e.value)
                  setValue('minimum_contract', e.value)
                }} 
                placeholder="Tempo mínimo"
              />
              {errors.minimum_contract ? (<div className="w-100"> <Error>{errors.minimum_contract?.message}</Error> </div>) : ""}
            </Col>
          </Row>

          <div className="mb-3">
            <label htmlFor="url_checkout" className="block">Link de checkout</label>
            <InputText placeholder="https://" id="url_checkout" {...register("url_checkout")} type="text" className="w-100" style={{ height: '40px'}} />
          </div>
          
          <Col md={12} className="mt-4">
            {
              loadingButton ? (
                <Spinner animation="border" variant="primary" style={{ width: '2rem', height: '2rem', borderWidth: '0.25em', borderRightColor: 'transparent' }} />
              ) : (
                <Button type="submit" data-testid="submit">
                  <span className="ml-3 mr-3">{translation('manager.settings.saveNew')}</span>
                </Button>
              )
            }
          </Col>
        </Form>
      </Col>
    </Row>
  )
}